/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Admin\index.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - INTERFACE D'ADMINISTRATION PRINCIPALE

📋 DESCRIPTION:
Interface d'administration principale avec navigation entre les différents modules.
Tableau de bord administrateur avec accès aux fonctionnalités de gestion.

🎯 RESPONSABILITÉS:
- Navigation entre les modules d'administration
- Tableau de bord avec aperçu des métriques importantes
- Accès rapide aux fonctionnalités critiques
- Interface responsive et intuitive

🔗 DÉPENDANCES:
- React Router: Navigation entre les sous-modules
- Composants Admin: GestionUtilisateurs, MonitoringFallbacks
- Tailwind CSS: Styles et composants UI

📡 INTERACTIONS:
- Routage vers /admin/users et /admin/monitoring
- Intégration avec les APIs de monitoring
- Gestion des permissions d'accès

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juillet 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface modulaire avec navigation par onglets
- Accès restreint aux administrateurs
- Métriques en temps réel
*/

import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import GestionUtilisateurs from './GestionUtilisateurs';
import MonitoringFallbacks from './MonitoringFallbacks';
import GestionFeedbacks from './GestionFeedbacks';
import UserDetail from './UserDetail';
import { apiFetch } from '../../services/httpClient';
import { buildApiUrl } from '../../config/api';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [newFeedbackCount, setNewFeedbackCount] = useState<number>(0);

  // Déterminer l'onglet actif basé sur l'URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/users')) {
      setActiveTab('users');
    } else if (path.includes('/monitoring')) {
      setActiveTab('monitoring');
    } else if (path.includes('/feedbacks')) {
      setActiveTab('feedbacks');
    } else {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  // Charger le nombre de nouveaux feedbacks
  useEffect(() => {
  const loadNewFeedbackCount = async () => {
    try {
      const data = await apiFetch<any>('/feedback/count/new');
      if (data.success) {
        setNewFeedbackCount(data.count);
        }
      } catch (error) {
        console.error('Erreur chargement count feedbacks:', error);
      }
    };

    loadNewFeedbackCount();
    // Actualiser toutes les 30 secondes
    const interval = setInterval(loadNewFeedbackCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Charger l'état de santé du système
  useEffect(() => {
    const loadSystemHealth = async () => {
      try {
        const data = await apiFetch<any>('/fallback/health');
        if (data.success) {
          setSystemHealth(data.health);
        }
      } catch (error) {
        console.error('Erreur chargement sant? syst?me:', error);
      }
    };

    loadSystemHealth();
    // Actualiser toutes les 30 secondes
    const interval = setInterval(loadSystemHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Navigation entre les onglets
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    switch (tab) {
      case 'overview':
        navigate('/admin');
        break;
      case 'users':
        navigate('/admin/users');
        break;
      case 'monitoring':
        navigate('/admin/monitoring');
        break;
      case 'feedbacks':
        navigate('/admin/feedbacks');
        break;
    }
  };

  const isUsersTab = activeTab === 'users' || location.pathname.includes('/admin/users');
  const contentContainerClass = isUsersTab
    ? 'max-w-none px-4 sm:px-6 lg:px-10'
    : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

  // Fonction pour obtenir la couleur de l'état de santé
  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Vue d'ensemble (dashboard principal)
  const OverviewContent = () => (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          🛠️ Administration Système
        </h1>
        <p className="text-gray-600">
          Tableau de bord administrateur pour la gestion et le monitoring du système.
        </p>
      </div>

      {/* État de santé du système */}
      {systemHealth && (
        <div className={`p-4 rounded-lg border ${getHealthColor(systemHealth.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {systemHealth.status === 'healthy' ? '✅' : 
                 systemHealth.status === 'warning' ? '⚠️' : 
                 systemHealth.status === 'critical' ? '🚨' : '❌'}
              </span>
              <div>
                <h3 className="font-semibold">État du système: {systemHealth.status.toUpperCase()}</h3>
                <p className="text-sm">{systemHealth.message}</p>
              </div>
            </div>
            <div className="text-right text-sm">
              <div>Fallbacks totaux: {systemHealth.metrics.total_fallbacks}</div>
              <div>Dernières 24h: {systemHealth.metrics.recent_24h}</div>
              <div>Critiques: {systemHealth.metrics.critical_count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Modules d'administration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gestion des utilisateurs */}
        <div className="bg-white p-6 rounded-lg shadow border hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-4">👥</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Gestion des Utilisateurs</h3>
              <p className="text-sm text-gray-600">Gérer les comptes, rôles et permissions</p>
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span>Utilisateurs actifs</span>
              <span className="font-semibold">-</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Administrateurs</span>
              <span className="font-semibold">-</span>
            </div>
          </div>
          <button
            onClick={() => handleTabChange('users')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Accéder à la gestion
          </button>
        </div>

        {/* Monitoring des fallbacks */}
        <div className="bg-white p-6 rounded-lg shadow border hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-4">📊</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Monitoring des Fallbacks</h3>
              <p className="text-sm text-gray-600">Surveillance des erreurs et fallbacks système</p>
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span>Alertes récentes</span>
              <span className="font-semibold">
                {systemHealth ? systemHealth.metrics.recent_24h : '-'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Alertes critiques</span>
              <span className="font-semibold text-red-600">
                {systemHealth ? systemHealth.metrics.critical_count : '-'}
              </span>
            </div>
          </div>
          <button
            onClick={() => handleTabChange('monitoring')}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
          >
            Accéder au monitoring
          </button>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Actions Rapides</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => handleTabChange('monitoring')}
            className="p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <span className="text-xl mr-3">🔄</span>
              <div>
                <div className="font-medium">Actualiser Monitoring</div>
                <div className="text-xs text-gray-500">Vérifier l'état système</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => handleTabChange('users')}
            className="p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <span className="text-xl mr-3">➕</span>
              <div>
                <div className="font-medium">Nouvel Utilisateur</div>
                <div className="text-xs text-gray-500">Créer un compte</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => window.open(buildApiUrl('/fallback/dashboard'), '_blank')}
            className="p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <span className="text-xl mr-3">📥</span>
              <div>
                <div className="font-medium">Export Données</div>
                <div className="text-xs text-gray-500">API JSON brute</div>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation par onglets */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Vue d'ensemble
            </button>
            <button
              onClick={() => handleTabChange('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Utilisateurs
            </button>
            <button
              onClick={() => handleTabChange('monitoring')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monitoring'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🚨 Monitoring
              {systemHealth && systemHealth.metrics.critical_count > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {systemHealth.metrics.critical_count}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('feedbacks')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'feedbacks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💬 Feedbacks
              {newFeedbackCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {newFeedbackCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Contenu principal */}
      <div className={`${contentContainerClass} py-6`}>
        <Routes>
          <Route index element={<OverviewContent />} />
          <Route path="users" element={<GestionUtilisateurs />} />
          <Route path="users/:id" element={<UserDetail />} />
          <Route path="monitoring" element={<MonitoringFallbacks />} />
          <Route path="feedbacks" element={<GestionFeedbacks />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;
